<template>
    <v-container v-resize="onResize">
        <statistical-header :title="title"></statistical-header>
        <loader v-if="loading" />
        <template v-if="!loading">
            <v-layout row wrap justify-space-between>
                <statistical-member-or-senator-selector :value="selectedMpOrSenator" @input="selectedMpOrSenator = $event" />
                <div class="d-flex">
                    <country-selector class="mr-3" :countries="countries" :value="countryFilter" @input="countryFilter = $event"></country-selector>
                    <parliament-selector :value="selectedParliament" :enableSelectAll="true" @input="selectedParliament = $event"></parliament-selector>
                </div>
            </v-layout>
            <br/>
            <world-map :chartData="mapData"/>
            <br/>
            <v-data-table 
                :headers="headers" 
                :items="itemsComputed"
                :rows-per-page-items="ROWS_LISTITEMS"
                :pagination.sync="PAGINATION"
                :hide-headers="isMobile"
                :class="{ mobileDataTable: isMobile, 'elevation-1': true }"
                :loading="loading"
                :disable-initial-sort="true">
                <template v-slot:items="props">
                    <template v-if="isMobile">
                            <tr>
                                <td>
                                    <div class="flex-content">
                                        <div class="flex-item">
                                            <h3 class="pb-xs-1"><router-link :to="{ name: $store.state.ROUTE_PARLIAMENTARIAN, params: { PHID: props.item.Phid }}">{{ props.item.Parliamentarian }}</router-link></h3>
                                        </div>
                                        <div class="flex-item flex-item-col flex-item-col-30">
                                            <div class="flex-item">
                                                <Portrait v-bind:PHID="props.item.Phid" :fixedWidth="80"></Portrait>
                                            </div>
                                        </div>
                                        <div class="flex-item flex-item-col flex-item-col-70">
                                            <div class="flex-item" data-label="Party">
                                                {{ props.item.Party }}
                                            </div>
                                            <div class="flex-item" data-label="Country of birth">
                                                {{ props.item.CountryOfBirth }}
                                            </div>
                                            <div class="flex-item" data-label="Period of service">
                                                <span class="d-block" v-for="service in props.item.Service" :key="props.item.Phid + service.DateStart + service.DateEnd">
                                                    <span v-if="service.DateStart != null">{{ service.DateStart | stringDateFormat }}</span>
                                                    <span v-if="service.DateStart == null">N/A</span>
                                                    <span>–</span>
                                                    <span v-if="service.DateEnd != null">{{ service.DateEnd | stringDateFormat }}</span>
                                                    <span v-if="service.DateEnd == null">present</span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    <template v-if="!isMobile">
                        <td class="text-xs-left">
                            <Portrait v-bind:fixedWidth="80" v-bind:PHID="props.item.Phid"></Portrait>
                        </td>
                        <td class="text-xs-left"><router-link :to="{ name: $store.state.ROUTE_PARLIAMENTARIAN, params: { PHID: props.item.Phid }}">{{ props.item.Parliamentarian }}</router-link></td>
                        <td class="text-xs-left">{{ props.item.Party }}</td>
                        <td class="text-xs-left">{{ props.item.CountryOfBirth }}</td>
                        <td class="text-xs-left handbook-tablecol_linebreaks">
                            <span class="d-block" v-for="service in props.item.Service" :key="props.item.Phid + service.DateStart + service.DateEnd">
                                <span v-if="service.DateStart != null">{{ service.DateStart | stringDateFormat }}</span>
                                <span v-if="service.DateStart == null">N/A</span>
                                <span>–</span>
                                <span v-if="service.DateEnd != null">{{ service.DateEnd | stringDateFormat }}</span>
                                <span v-if="service.DateEnd == null">present</span>
                            </span>
                        </td>
                    </template>
                </template>
            </v-data-table>
            <br/>
            <v-layout row wrap>
                <v-flex xs12>
                    <v-btn class="csvButton" @click="exportCsv" v-on:keyup.enter="exportCsv" color="primary">
                        <v-icon aria-hidden="true" :class="csvDownloadClass">{{csvDownloadIcon}}</v-icon>
                        <span>&nbsp;Download as CSV</span>
                    </v-btn>
                </v-flex>
            </v-layout>             
        </template>
    </v-container>
</template>
<script>

import Loader from '@/components/Loader.vue';
import StatisticalHeader from './StatisticalHeader.vue';
import StatisticalMemberOrSenatorSelector from './StatisticalMemberOrSenatorSelector.vue';
import ParliamentSelector from './ParliamentSelector.vue';
import CountrySelector from './CountrySelector.vue';
import WorldMap from './WorldMap.vue';
import axios from 'axios';
import Portrait from '@/components/Portrait.vue'
window.Handbook = window.Handbook || {}
const apiUri = window.Handbook.ApiUri || "https://localhandbookapi.aph.gov.au/";
axios.defaults.baseURL = apiUri;

export default {
    created() {
        this.initialiseItems();
    },
    components: {
        Loader,        
        WorldMap,
        StatisticalHeader,
        StatisticalMemberOrSenatorSelector,
        ParliamentSelector,
        CountrySelector,
        Portrait,
    },
    methods: {
        initialiseItems() {
            this.loading = true;
            const parliamentarianType = this.selectedMpOrSenator.length == 0 || this.selectedMpOrSenator.length == 2 ?
                '' : this.selectedMpOrSenator[0];
            const url = `StatisticalInformation/OverseasBorn?parliamentariantype=${parliamentarianType}&parliament=${this.selectedParliament ? this.selectedParliament : 0}`;
            axios.get(`api/${url}`)
                .then(response => {
                    this.items = response.data;
                    this.loading = false;
                });
        },
        filterMpOrSenator() {
            this.initialiseItems();
        },
        exportCsv: function () {
            this.csvDownloading = true;
            const records = this.itemsComputed.map(i => ({ 
                Phid: i.Phid,
                Name: i.Parliamentarian, 
                Party: i.Party, 
                'Period of service': i.Service.map(s => `${new Date(s.DateStart).toLocaleDateString()} - ${s.DateEnd ? new Date(s.DateEnd).toLocaleDateString() : 'present' }`).join('\n'),
                'Country of birth': i.CountryOfBirth }));
            let headers = this.headers.map(h => h.text);
            const filename = 'Overseas born';
            const comment = `Data current as of ${(new Date()).toLocaleDateString()}`;
            this.$store.dispatch('GETCSV', { DATA: records, HEADERS: headers, FILENAME: filename, COMMENT: comment })
                .catch(this.showError)
                .finally(() => this.csvDownloading = false);
        } ,
        onResize() {
            if (window.innerWidth < 769){
                this.isMobile = true;
            }
            else{
                this.isMobile = false;
            }
        },       
    }, 
    mounted() {
        this.onResize();
    },
    computed: {
        csvDownloadIcon(){
            return this.csvDownloading ? 'cached' : 'text_snippet';
        },
        csvDownloadClass() {
            let csvClass = 'mr-1';
            if (this.csvDownloading){
                csvClass = csvClass + ' loading'
            }
            return csvClass;
        },
        filteredItems() {
            return this.countryFilter ? 
            (this.items.filter(i => this.countryFilter === 'United Kingdom' ? 
            (i.CountryOfBirth === this.countryFilter || i.CountryOfBirth === 'England' || i.CountryOfBirth === 'Scotland' || i.CountryOfBirth === 'Wales' || i.CountryOfBirth === 'Northern Ireland' )
            :  
            i.CountryOfBirth === this.countryFilter))
            : 
            this.items;
        },
        itemsComputed() {
            const result = [];
            this.filteredItems.forEach(item => {
                // if first, just add
                const service = [];
                item.Service.forEach((s, i) => {
                    if (i === 0) {
                        service.push(s);
                    } else {
                        // if previous item overlaps, just push the end date out
                        const previousItem = item.Service[i - 1];
                        if ( new Date(previousItem.DateEnd).addDays(1) >= new Date(s.DateStart) ) {
                            service[service.length - 1].DateEnd = s.DateEnd
                        } else {
                            service.push(s);
                        }
                    }
                });
                result.push({...item, Service: service});
            });
            // merge adjacent service together
            return result;
        },
        countries() {
            return [...new Set(this.items.map(i => i.CountryOfBirth))]
        },
        mapData() {
            const data = this.items.map(i => i.CountryCode);
            let dedupedData = [...new Set(data)].map(d => ({ "code3": d, "z": 0 }));
            data.forEach(d => {
                const row = dedupedData.find(dd => dd.code3 === d);
                row.z++;
            });
            var that = this;

            return {
                title: {
                    text: this.title
                },
                plotOptions: {
                    series: {
                        point: {
                            events: {
                                click: e => {
                                    that.countryFilter = e.point.name;
                                }
                            }
                        }
                    }
                },
                exporting: {
                    filename: 'Overseas born'
                },               
                series: [{
                name: 'Countries',
                color: '#E0E0E0',
                enableMouseTracking: false
            }, {
                name: 'Parliamentarians born overseas',
                type: 'mapbubble',
                joinBy: ['iso-a3', 'code3'],
                data: dedupedData,
                minSize: 4,
                maxSize: '12%',
                tooltip: {
                    pointFormat: '{point.properties.name}: {point.z}'
                }
            }],

            };
        }
    },   
    watch: {
        selectedMpOrSenator: function () {
            this.initialiseItems();
        },
        selectedParliament: function () {
            this.initialiseItems();
        }
    },     
    data() {
        return {
        title: 'Overseas born',
        isMobile: false,
        headers: [
            {
                text: '',
                value: 'Parliamentarian',
                sortable: false
            },
            {
                text: 'Name',
                value: 'Parliamentarian'
            },
            {
                text: 'Party',
                value: 'Party' 
            },
            {
                text: 'Country of birth',
                value: 'CountryOfBirth'
            },
            {
                text: 'Period of service',
                value: 'Service'
            }
        ],
        selectedParliament: 47,
        items: [],
        search: '',
        countryFilter: '',
        loading: true,
        csvDownloading: false,
        selectedMpOrSenator: ['senator', 'mp'],
        PAGINATION: {
            rowsPerPage: -1
        },
        ROWS_LISTITEMS: [5, 10, 50, {
            "text": "$vuetify.dataIterator.rowsPerPageAll",
            "value": -1
        }],
        }
    }       
}
</script>
<style scoped>
    a {
        text-decoration: none;
    }
</style>